/**
 * MyCopro - User Store
 * Gestion des utilisateurs/membres de la résidence
 */

import { create } from 'zustand';
import { 
  ResidenceMember, 
  UserCategoryType,
  CategoryChangeRequest,
  CategoryChangeStatus
} from '../types';

// ============================================================================
// TYPES DU STORE
// ============================================================================

interface UserState {
  // Liste des membres de la résidence
  members: ResidenceMember[];
  
  // Demandes de changement de catégorie en attente
  categoryChangeRequests: CategoryChangeRequest[];
  
  // État de chargement
  isLoading: boolean;
  error: string | null;
  
  // Filtres
  filterCategory: UserCategoryType | 'all';
  searchTerm: string;
}

interface UserActions {
  // Chargement
  setMembers: (members: ResidenceMember[]) => void;
  setCategoryChangeRequests: (requests: CategoryChangeRequest[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Gestion des membres
  addMember: (member: ResidenceMember) => void;
  updateMember: (id: string, data: Partial<ResidenceMember>) => void;
  removeMember: (id: string) => void;
  
  // Activation/Désactivation
  toggleMemberEnabled: (id: string) => void;
  setMemberEnabled: (id: string, enabled: boolean) => void;
  
  // Changement de catégorie
  changeMemberCategory: (id: string, category: UserCategoryType) => void;
  
  // Demandes de changement
  addCategoryChangeRequest: (request: CategoryChangeRequest) => void;
  processCategoryChangeRequest: (id: string, status: CategoryChangeStatus, processedBy: string) => void;
  removeCategoryChangeRequest: (id: string) => void;
  
  // Filtres
  setFilterCategory: (category: UserCategoryType | 'all') => void;
  setSearchTerm: (term: string) => void;
  
  // Reset
  reset: () => void;
}

type UserStore = UserState & UserActions;

// ============================================================================
// ÉTAT INITIAL
// ============================================================================

const initialState: UserState = {
  members: [],
  categoryChangeRequests: [],
  isLoading: false,
  error: null,
  filterCategory: 'all',
  searchTerm: ''
};

// ============================================================================
// STORE
// ============================================================================

export const useUserStore = create<UserStore>((set, get) => ({
  ...initialState,

  // ========================================
  // CHARGEMENT
  // ========================================
  
  setMembers: (members) => {
    set({ members, isLoading: false, error: null });
  },

  setCategoryChangeRequests: (categoryChangeRequests) => {
    set({ categoryChangeRequests });
  },

  setLoading: (isLoading) => {
    set({ isLoading });
  },

  setError: (error) => {
    set({ error, isLoading: false });
  },

  // ========================================
  // GESTION DES MEMBRES
  // ========================================
  
  addMember: (member) => {
    set(state => ({
      members: [...state.members, member]
    }));
  },

  updateMember: (id, data) => {
    set(state => ({
      members: state.members.map(m => 
        m.id === id 
          ? { ...m, ...data, updatedAt: new Date().toISOString() } 
          : m
      )
    }));
  },

  removeMember: (id) => {
    set(state => ({
      members: state.members.filter(m => m.id !== id)
    }));
  },

  // ========================================
  // ACTIVATION/DÉSACTIVATION
  // ========================================
  
  toggleMemberEnabled: (id) => {
    set(state => ({
      members: state.members.map(m => 
        m.id === id 
          ? { ...m, enabled: !m.enabled, updatedAt: new Date().toISOString() } 
          : m
      )
    }));
  },

  setMemberEnabled: (id, enabled) => {
    set(state => ({
      members: state.members.map(m => 
        m.id === id 
          ? { ...m, enabled, updatedAt: new Date().toISOString() } 
          : m
      )
    }));
  },

  // ========================================
  // CHANGEMENT DE CATÉGORIE
  // ========================================
  
  changeMemberCategory: (id, category) => {
    set(state => ({
      members: state.members.map(m => 
        m.id === id 
          ? { ...m, category, updatedAt: new Date().toISOString() } 
          : m
      )
    }));
  },

  // ========================================
  // DEMANDES DE CHANGEMENT
  // ========================================
  
  addCategoryChangeRequest: (request) => {
    set(state => ({
      categoryChangeRequests: [...state.categoryChangeRequests, request]
    }));
  },

  processCategoryChangeRequest: (id, status, processedBy) => {
    const { categoryChangeRequests, members } = get();
    const request = categoryChangeRequests.find(r => r.id === id);
    
    if (!request) return;
    
    // Mettre à jour la demande
    const updatedRequests = categoryChangeRequests.map(r => 
      r.id === id 
        ? { 
            ...r, 
            status, 
            processedAt: new Date().toISOString(),
            processedBy 
          } 
        : r
    );
    
    // Si approuvé, mettre à jour le membre
    let updatedMembers = members;
    if (status === 'approved') {
      updatedMembers = members.map(m => 
        m.odile === request.odile
          ? {
              ...m,
              category: request.requestedCategory,
              apartment: request.apartment || m.apartment,
              email: request.email || m.email,
              residentId: request.residentId || m.residentId,
              landlordType: request.landlordType,
              landlordId: request.landlordId,
              updatedAt: new Date().toISOString()
            }
          : m
      );
    }
    
    set({
      categoryChangeRequests: updatedRequests,
      members: updatedMembers
    });
  },

  removeCategoryChangeRequest: (id) => {
    set(state => ({
      categoryChangeRequests: state.categoryChangeRequests.filter(r => r.id !== id)
    }));
  },

  // ========================================
  // FILTRES
  // ========================================
  
  setFilterCategory: (filterCategory) => {
    set({ filterCategory });
  },

  setSearchTerm: (searchTerm) => {
    set({ searchTerm });
  },

  // ========================================
  // RESET
  // ========================================
  
  reset: () => {
    set(initialState);
  }
}));

// ============================================================================
// SÉLECTEURS
// ============================================================================

export const selectMembers = (state: UserStore) => state.members;
export const selectCategoryChangeRequests = (state: UserStore) => state.categoryChangeRequests;
export const selectIsLoading = (state: UserStore) => state.isLoading;
export const selectError = (state: UserStore) => state.error;

// Sélecteurs filtrés
export const selectFilteredMembers = (state: UserStore): ResidenceMember[] => {
  let filtered = state.members;
  
  // Filtre par catégorie
  if (state.filterCategory !== 'all') {
    filtered = filtered.filter(m => m.category === state.filterCategory);
  }
  
  // Filtre par recherche
  if (state.searchTerm.trim()) {
    const term = state.searchTerm.toLowerCase();
    filtered = filtered.filter(m => 
      m.name.toLowerCase().includes(term) ||
      (m.apartment?.toLowerCase().includes(term)) ||
      m.email.toLowerCase().includes(term)
    );
  }
  
  return filtered;
};

// Sélecteurs de comptage
export const selectMemberCount = (state: UserStore): number => 
  state.members.length;

export const selectEnabledMemberCount = (state: UserStore): number => 
  state.members.filter(m => m.enabled).length;

export const selectCategoryStats = (state: UserStore): Record<UserCategoryType, number> => {
  const stats: Record<UserCategoryType, number> = {
    tenant: 0,
    owner: 0,
    council: 0,
    syndic: 0,
    gardien: 0,
    admin: 0
  };
  
  state.members.forEach(m => {
    stats[m.category]++;
  });
  
  return stats;
};

export const selectPendingCategoryChanges = (state: UserStore): CategoryChangeRequest[] => 
  state.categoryChangeRequests.filter(r => r.status === 'pending');

export const selectPendingCategoryChangesCount = (state: UserStore): number => 
  state.categoryChangeRequests.filter(r => r.status === 'pending').length;

// Sélecteur par ID
export const selectMemberById = (id: string) => (state: UserStore): ResidenceMember | undefined => 
  state.members.find(m => m.id === id);

export const selectMemberByUid = (odile: string) => (state: UserStore): ResidenceMember | undefined => 
  state.members.find(m => m.odile === odile);
